import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import type { CompetitorProduct } from "@/graphql/codegen/graphql"
import type { CompetitorProductFormValues } from "@/screens/ManageProducts/components/CompetitorProducts/forms/competitorProductSchema"
import { useCompetitorProductActions } from "@/screens/ManageProducts/components/CompetitorProducts/hooks/useCompetitorProductActions"
import {
  serializeCreateInput,
  serializeUpdateInput,
} from "@/screens/ManageProducts/components/CompetitorProducts/utils"

interface UseCompetitorProductDialogActionsProps {
  onSuccess: ((data: CompetitorProduct) => Promise<void>) | undefined
  onClose: () => void
  methods: UseFormReturn<CompetitorProductFormValues>
  data?: CompetitorProduct
}

interface UseCompetitorProductDialogActionsReturn {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
}

const useCompetitorProductDialogActions = ({
  onSuccess,
  onClose,
  methods,
  data,
}: UseCompetitorProductDialogActionsProps): UseCompetitorProductDialogActionsReturn => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const isCreateMode = !data
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { createCompetitorProductData, updateCompetitorProductData } = useCompetitorProductActions({
    onSuccess: async (competitorProduct) => {
      if (competitorProduct) {
        await onSuccess?.(competitorProduct)
      }
      onClose()
    },
  })

  const handleSubmit = zodHandleSubmit(async (input: CompetitorProductFormValues) => {
    if (isCreateMode) {
      await createCompetitorProductData(serializeCreateInput(input))
    } else {
      await updateCompetitorProductData(serializeUpdateInput(input, data.competitorProductId))
    }
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  // Ensure the form is valid, dirty, and has no errors before allowing submission
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}

export default useCompetitorProductDialogActions
